import { z } from 'zod';
import { validateRRule } from './recurrence.js';
import { isLocalDate, isLocalTime } from './time.js';

export const localDateSchema = z.string().refine(isLocalDate, 'Neveljaven datum (YYYY-MM-DD).');
export const localTimeSchema = z.string().refine(isLocalTime, 'Neveljavna ura (HH:MM).');

export const rruleSchema = z
  .string()
  .trim()
  .min(1)
  .superRefine((value, ctx) => {
    const error = validateRRule(value);
    if (error) ctx.addIssue({ code: 'custom', message: error });
  });

export const itemKindSchema = z.enum(['activity', 'habit']);
export const prioritySchema = z.enum(['must', 'flex']);
export const checkinStatusSchema = z.enum(['done', 'missed', 'skipped']);

/** Palette keys; the UI maps them to light/dark colours. */
export const ITEM_COLORS = [
  'blue',
  'green',
  'orange',
  'red',
  'purple',
  'pink',
  'teal',
  'gray',
] as const;
export const itemColorSchema = z.enum(ITEM_COLORS);
export type ItemColor = z.infer<typeof itemColorSchema>;

export const ANCHOR_TYPES = ['time', 'anytime', 'after', 'wake', 'bed'] as const;
export const anchorTypeSchema = z.enum(ANCHOR_TYPES);
export type AnchorType = z.infer<typeof anchorTypeSchema>;
export const anchorFallbackSchema = z.enum(['skip', 'anytime', 'wake']);
export type AnchorFallback = z.infer<typeof anchorFallbackSchema>;

const titleSchema = z.string().trim().min(1, 'Ime je obvezno.').max(120);
const notesSchema = z.string().trim().max(2000);

const itemFields = {
  title: titleSchema,
  kind: itemKindSchema,
  startTime: localTimeSchema,
  durationMin: z
    .number()
    .int()
    .min(0)
    .max(24 * 60),
  rrule: rruleSchema.nullable(),
  startDate: localDateSchema,
  endDate: localDateSchema.nullable(),
  requiresCheck: z.boolean(),
  reminderMin: z
    .number()
    .int()
    .min(0)
    .max(7 * 24 * 60)
    .nullable(),
  priority: prioritySchema,
  color: itemColorSchema.nullable(),
  notes: notesSchema.nullable(),
  /** Block of a day template: shown on every day that uses the template (rrule must be null). */
  templateId: z.uuid().nullable(),
  /** Counts towards a weekly goal. */
  goalId: z.uuid().nullable(),
  /** Scheduled inbox task (time-boxed). */
  taskId: z.uuid().nullable(),
  /** Repeating item that also happens on holidays (days with a pausing all-day event). */
  keepOnPause: z.boolean(),
  /** Travel there before the start and back after the end (minutes). */
  travelBeforeMin: z.number().int().min(0).max(600),
  travelAfterMin: z.number().int().min(0).max(600),
  /**
   * What sets the time: a clock time (`startTime`), none ("kadarkoli danes"), the end of another
   * activity ("po Fitnesu"), getting up or going to bed (from the sleep times of that day).
   */
  anchorType: anchorTypeSchema,
  /** For `after`: the activity (any item of its series). */
  anchorItemId: z.uuid().nullable(),
  /** Minutes after the activity / after getting up / before going to bed. */
  anchorOffsetMin: z
    .number()
    .int()
    .min(0)
    .max(12 * 60),
  /** For `after`, on days without that activity: not at all, any time, or at wake-up. */
  anchorFallback: anchorFallbackSchema,
};

const endNotBeforeStart = (v: { startDate?: string; endDate?: string | null }) =>
  !v.endDate || !v.startDate || v.endDate >= v.startDate;

export const itemInputSchema = z
  .object({
    ...itemFields,
    durationMin: itemFields.durationMin.default(0),
    rrule: itemFields.rrule.default(null),
    endDate: itemFields.endDate.default(null),
    requiresCheck: itemFields.requiresCheck.default(true),
    reminderMin: itemFields.reminderMin.default(null),
    priority: itemFields.priority.default('flex'),
    color: itemFields.color.default(null),
    notes: itemFields.notes.default(null),
    templateId: itemFields.templateId.default(null),
    goalId: itemFields.goalId.default(null),
    taskId: itemFields.taskId.default(null),
    keepOnPause: itemFields.keepOnPause.default(false),
    travelBeforeMin: itemFields.travelBeforeMin.default(0),
    travelAfterMin: itemFields.travelAfterMin.default(0),
    anchorType: itemFields.anchorType.default('time'),
    anchorItemId: itemFields.anchorItemId.default(null),
    anchorOffsetMin: itemFields.anchorOffsetMin.default(0),
    anchorFallback: itemFields.anchorFallback.default('skip'),
  })
  .strict()
  .refine(endNotBeforeStart, { message: 'Konec veljavnosti je pred začetkom.', path: ['endDate'] })
  .refine((v) => v.anchorType !== 'after' || Boolean(v.anchorItemId), {
    message: 'Izberi aktivnost, po kateri pride.',
    path: ['anchorItemId'],
  })
  .refine((v) => !v.templateId || !v.rrule, {
    message: 'Blok predloge se ne ponavlja sam.',
    path: ['rrule'],
  });
export type ItemInput = z.input<typeof itemInputSchema>;

export const itemSchema = z.object({
  id: z.uuid(),
  /** Same for all parts of a series split by "ta in naslednji" (anchors follow the series). */
  seriesId: z.uuid().optional(),
  ...itemFields,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Item = z.infer<typeof itemSchema>;

/** "samo ta" is handled by exceptions; these scopes apply to the series. */
export const editScopeSchema = z.enum(['all', 'following']);
export type EditScope = z.infer<typeof editScopeSchema>;

export const itemPatchSchema = z
  .object({
    scope: editScopeSchema.default('all'),
    /** Required for scope "following": first date the change applies to. */
    fromDate: localDateSchema.optional(),
    changes: z.object(itemFields).omit({ templateId: true, taskId: true }).partial().strict(),
  })
  .strict()
  .refine((v) => v.scope === 'all' || v.fromDate, {
    message: 'Za "ta in naslednji" je potreben datum.',
    path: ['fromDate'],
  });
export type ItemPatch = z.input<typeof itemPatchSchema>;

export const exceptionInputSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('skip') }).strict(),
  z
    .object({
      action: z.literal('move'),
      newTime: localTimeSchema,
      newDurationMin: z
        .number()
        .int()
        .min(0)
        .max(24 * 60)
        .nullable()
        .default(null),
      /** Move to another day (at most two weeks away); null = same day. */
      newDate: localDateSchema.nullable().default(null),
    })
    .strict(),
]);

/** Furthest a single occurrence can be moved to another day. */
export const MAX_MOVE_DAYS = 14;
export type ExceptionInput = z.input<typeof exceptionInputSchema>;

export const itemExceptionSchema = z.object({
  itemId: z.uuid(),
  date: localDateSchema,
  action: z.enum(['skip', 'move']),
  newTime: localTimeSchema.nullable(),
  newDurationMin: z.number().int().nullable(),
  newDate: localDateSchema.nullable(),
});
export type ItemException = z.infer<typeof itemExceptionSchema>;

export const checkinTargetSchema = z.enum(['item', 'event']);

export const checkinSchema = z.object({
  /** Own item (default) or an imported calendar event. */
  target: checkinTargetSchema.default('item'),
  /** Item id, or external event id when `target` is 'event'. */
  itemId: z.uuid(),
  date: localDateSchema,
  /** null = cleared ("ni še označeno"). */
  status: checkinStatusSchema.nullable(),
  reason: z.string().trim().max(500).nullable(),
  /** Client time of the change (ISO); later changes win when syncing offline edits. */
  clientTs: z.iso.datetime({ offset: true }),
});
export type Checkin = z.infer<typeof checkinSchema>;
export type CheckinInput = z.input<typeof checkinSchema>;
export type CheckinStatus = z.infer<typeof checkinStatusSchema>;

export const checkinBatchSchema = z.object({ checkins: z.array(checkinSchema).min(1).max(500) });

export const dateRangeQuerySchema = z
  .object({ from: localDateSchema, to: localDateSchema })
  .refine((v) => v.from <= v.to, { message: 'from > to' });

export const loginSchema = z.object({ password: z.string().min(1).max(256) }).strict();

export const passwordChangeSchema = z
  .object({ current: z.string().min(1).max(256), next: z.string().min(1).max(256) })
  .strict();
export type PasswordChange = z.infer<typeof passwordChangeSchema>;

export interface SessionInfo {
  /** Short public handle (not the session token). */
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

export interface SecurityInfo {
  /** env = password from APP_PASSWORD_HASH_B64; app = changed in the app. */
  passwordSource: 'env' | 'app';
  passwordChangedAt: string | null;
  /** APP_PASSWORD_RESET is still set on the server. */
  resetActive: boolean;
  sessions: SessionInfo[];
}

export const settingsSchema = z.object({ timezone: z.string() });
export type Settings = z.infer<typeof settingsSchema>;
